"use client";

import { Track } from "./index";

/**
 * 声明式 DOM 契约 —— 曝光 `data-or-view`、点击业务事件 `data-or-event`，参数共用 `data-or-param-*`。
 *
 * 规范：`docs/engineering/tracking/tracking-conventions.md` §1.2 / §1.4 / §5。
 *
 * | 场景 | 用什么 | 谁读 |
 * |------|--------|------|
 * | 曝光 | `data-or-view="event_name"` | 本文件（IntersectionObserver） |
 * | 点击（无参） | `data-openreplay-label` | OpenReplay SDK 自动出 `MouseClick` |
 * | 点击（**带参**） | 上面那个 **+** `data-or-event="event_name"` | 本文件（capture 阶段 click 委托） |
 *
 * 点击带参必须另发一条事件，是**协议层的硬约束**：`MouseClick`(68) 没有 payload 字段，
 * 只有 `CustomEvent`(27) 有。所以「点击时带上 card_id」不可能挂在点击本体上。
 * 没写 `data-or-event` 的元素**什么都不多发** —— 不违反规范 §5。
 *
 * ⚠️ **参数值一律是字符串**（DOM 属性本来就只能存字符串）：
 * `data-or-param-count={3}` 上报的是 `"3"` 而不是 `3`。同一个 payload key
 * 如果既走这条声明式路径、又走 `Track.event(name, {count: 3})`，后台就会出现两种类型。
 * **同一个 key 只走一条路径**；需要数值语义的走命令式。
 *
 * ## ⚠️ `data-or-param-*` 只跟着 `data-or-view` / `data-or-event` 走
 *
 * [readParams] 读的是**声明节点自身**的属性，而声明节点是「带 `data-or-view` 的元素」或
 * 「`closest('[data-or-event]')` 命中的元素」。所以：
 *
 * - 只挂 `data-openreplay-label` + `data-or-param-*` → **参数被完全忽略**。
 *   `data-openreplay-label` 是 **OpenReplay SDK** 读的（`getLabelAttribute()` 硬编码），
 *   它不认识 `data-or-param-*`，`MouseClick` 协议层也没有 payload 位。
 * - 参数挂在父/子元素上 → 同样读不到，[readParams] 不向上或向下找。
 *
 * **三个属性必须在同一个元素上。** 写错了没有任何报错 —— 静态上一个孤儿
 * `data-or-param-*` 和一个普通 data 属性没有区别，门禁也查不出来。
 *
 * ## 语义
 *
 * - 元素**首次进入视口 ≥1%** 时上报一次，`event_type = "view"`
 * - **每个元素只报一次**（`WeakSet` 去重），列表滚动来回不会重复计数
 * - 参数从 `data-or-param-<key>` 读，`<key>` 直接作为 payload 的字段名
 *
 * ```tsx
 * <div data-or-view="goal_detail_view" data-or-param-goal_id={id} data-or-param-status={status} />
 * ```
 */

const VIEW_ATTR = "data-or-view";
const PARAM_PREFIX = "data-or-param-";
const SELECTOR = `[${VIEW_ATTR}]`;

/**
 * 点击的**业务事件名**（`data-or-event="brief_card_action"`）。
 *
 * 参数复用 [PARAM_PREFIX]，**不另起 `data-or-click-param-*`**：点击与曝光通常本来就在不同元素上
 * （卡片根节点管曝光、按钮管点击）；真在同一元素时是同一上下文、参数本就该是同一份。
 * 要带不同参数就拆元素 —— 那本来也是更清晰的结构。
 */
const CLICK_EVENT_ATTR = "data-or-event";
const CLICK_SELECTOR = `[${CLICK_EVENT_ATTR}]`;

/** 进入视口多少算"曝光"。1% —— 与业务口径无关的最低可见阈值，需要更严的场景自己发 `Track.view`。 */
const VISIBILITY_THRESHOLD = 0.01;

function readParams(el: Element): Record<string, string> {
  const out: Record<string, string> = {};
  for (const attr of Array.from(el.attributes)) {
    if (attr.name.startsWith(PARAM_PREFIX)) {
      out[attr.name.slice(PARAM_PREFIX.length)] = attr.value;
    }
  }
  return out;
}

/**
 * 装上**点击业务事件**监听。返回卸载函数。
 *
 * ## 为什么需要它
 *
 * `data-openreplay-label` 只给 SDK 自动发的 `MouseClick` 消息**命名**，而那个消息在协议层
 * **没有 payload 字段**（只有 `label / selector / x / y / hesitationTime`）。
 * 所以「点击时带上 card_id」必须另发一条 `CustomEvent` —— 唯一有 payload 的消息类型。
 *
 * 改之前这件事只能在 `onClick` 里手写第二个调用，靠自觉；现在写属性即可：
 *
 * ```tsx
 * <button
 *   data-openreplay-label="brief_card_track"   // 点击本体（SDK 读）
 *   data-or-event="brief_card_action"           // 业务事件名（本文件读）
 *   data-or-param-card_id={card.id}
 *   data-or-param-action="track"
 * />
 * ```
 *
 * ## 业务事件为什么自己起名，而不是复用 label
 *
 * 因为它们**确实是两件事**：`brief_card_track` 是「谁点了那颗按钮」，
 * `brief_card_action` 是「一张卡被 track 了」。复用 label 当事件名会与 click 消息同名，
 * 后台按名聚合时**数字翻倍**（`scripts/lint-name-collisions.mjs` 防的正是这个）；
 * 而收成一个固定名（如 `user_click`）又等于把「这是什么事」降级进 payload，
 * 白白浪费 `name` 这个一等字段。
 *
 * ## 这不违反 §5「一个动作不采两次」
 *
 * 两条记录**携带不同信息** —— click 消息有 selector / 坐标 / 犹豫时长，业务事件有 card_id，
 * 是互补不是冗余。只有「无参数还硬发第二条」才是纯重复。
 * 没写 `data-or-event` 的元素**什么都不多发**，与改动前一致。
 *
 * ## 用事件委托而不是逐元素绑
 *
 * SPA 不断插节点，逐元素绑要跟着 MutationObserver 补绑、卸载还要记账。
 * 委托到 `document` 只有一个监听器，`closest()` 向上找最近的声明节点 ——
 * 顺带解决「点在按钮内的 `<span>`/图标上」这种命中子节点的情况。
 */
export function installClickEventTracking(): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const onClick = (e: MouseEvent) => {
    const target = e.target;
    if (!(target instanceof Element)) {
      return;
    }
    // 点中的可能是按钮里的 span / svg，向上找最近的声明节点
    const el = target.closest(CLICK_SELECTOR);
    if (!el) {
      return;
    }
    const name = el.getAttribute(CLICK_EVENT_ATTR);
    if (name) {
      Track.event(name, readParams(el));
    }
  };

  // capture 阶段：业务 onClick 里常有 stopPropagation（brief 卡的按钮就是），
  // 冒泡阶段监听会被它掐掉 —— 埋点不该受业务的事件传播控制影响。
  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}

/**
 * 装上曝光监听。返回卸载函数。
 *
 * SSR / 不支持 IntersectionObserver 的环境直接 no-op（返回空函数），不抛。
 */
export function installViewTracking(): () => void {
  if (
    typeof window === "undefined" ||
    typeof IntersectionObserver === "undefined"
  ) {
    return () => {};
  }

  const reported = new WeakSet<Element>();

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = entry.target;
        if (!entry.isIntersecting || reported.has(el)) {
          continue;
        }
        reported.add(el);
        io.unobserve(el); // 报过就不用再盯着，省掉后续回调
        const name = el.getAttribute(VIEW_ATTR);
        if (name) {
          Track.view(name, readParams(el));
        }
      }
    },
    { threshold: VISIBILITY_THRESHOLD },
  );

  const observeIn = (root: ParentNode) => {
    root.querySelectorAll?.(SELECTOR).forEach((el) => io.observe(el));
  };

  observeIn(document);

  // SPA 会不断插入节点，新节点也要纳管。
  // 这是本方案唯一往主线程加的常驻监听 —— 必须过性能门禁（方案 §6.1）；
  // 不过关就退化成"路由变化时重扫"（行为等价，只是曝光时机略滞后）。
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of Array.from(m.addedNodes)) {
        if (!(node instanceof Element)) {
          continue;
        }
        if (node.hasAttribute(VIEW_ATTR)) {
          io.observe(node);
        }
        observeIn(node);
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  return () => {
    io.disconnect();
    mo.disconnect();
  };
}
